import { useThree } from "@react-three/fiber";
import { useContext, useEffect } from "react";
import { ProductStoreContext } from "./ProductStoreContext";

/**
 * Whether the user is orbiting the camera, for the hover glow of draggable
 * parts: while it holds, a part sweeping under the cursor neither lights up
 * nor reports its hover, and a glow already shown goes out for the gesture.
 * Every glow mounts tint materials, mesh clones and possibly an x-ray slot,
 * and parts cross the cursor many times per orbit, which made it stutter.
 *
 * `isUserControllingCamera` alone is not enough: it rises on the press, and a
 * plain click on a part would then make its glow flicker. The gate opens once
 * the pointer of the gesture has travelled farther than a click may: a press
 * that `DraggablePart` and react-three-fiber still accept as a click never
 * touches the hover. camera-controls rotates from the first pixel, so within
 * that distance the view has barely moved.
 */
export interface OrbitHoverGateState {
  /** `isUserControllingCamera` of the product store. */
  readonly controlling: boolean;
  /** Where the last press landed, in client pixels. */
  readonly press: { readonly x: number; readonly y: number } | null;
  /** The gesture is an orbit: hover stays off until the control ends. */
  readonly orbiting: boolean;
}

export type OrbitHoverGateEvent =
  | { type: "press"; x: number; y: number }
  | { type: "move"; x: number; y: number }
  | { type: "controlling"; controlling: boolean };

/**
 * Max pointer travel of a click: `DraggablePart` activates on an `onClick`
 * whose `delta` is at most this, and r3f rounds that delta to whole pixels.
 */
export const ORBIT_HOVER_CLICK_TRAVEL_PX = 2;

export const IDLE_ORBIT_HOVER_GATE: OrbitHoverGateState = {
  controlling: false,
  press: null,
  orbiting: false,
};

export function reduceOrbitHoverGate(
  state: OrbitHoverGateState,
  event: OrbitHoverGateEvent,
): OrbitHoverGateState {
  switch (event.type) {
    case "press":
      // A second finger joins the gesture; an orbit stays one.
      return { ...state, press: { x: event.x, y: event.y } };
    case "controlling":
      if (event.controlling === state.controlling) return state;
      return event.controlling
        ? { controlling: true, press: state.press, orbiting: false }
        : IDLE_ORBIT_HOVER_GATE;
    case "move": {
      if (!state.controlling || state.orbiting || !state.press) return state;
      const travel = Math.hypot(
        event.x - state.press.x,
        event.y - state.press.y,
      );
      return Math.round(travel) > ORBIT_HOVER_CLICK_TRAVEL_PX
        ? { ...state, orbiting: true }
        : state;
    }
  }
}

/** The product store's camera-control flag, as the gate reads it. */
export interface OrbitHoverGateSource {
  getControlling(): boolean;
  /** Calls the listener whenever the flag may have changed. */
  subscribe(listener: () => void): () => void;
}

export interface OrbitHoverGatePointer {
  readonly x: number;
  readonly y: number;
  /**
   * The pointer can hover: a mouse, not a touch, and not cancelled. A lifted
   * finger or pen leaves r3f's hover, but its last move stays the one r3f
   * would replay.
   */
  readonly hovers: boolean;
}

const pointerOf = (event: PointerEvent): OrbitHoverGatePointer => ({
  x: event.clientX,
  y: event.clientY,
  hovers: event.pointerType !== "touch",
});

interface OrbitHoverGateAttachment {
  source: OrbitHoverGateSource;
  /**
   * Re-runs the hover test once an orbit ends, so the part under the resting
   * cursor lights up without another move, and drops the hover where it
   * cannot. Gets where the pointer is now, in client pixels, or `null` if no
   * pointer event was seen.
   */
  resync: (pointer: OrbitHoverGatePointer | null) => void;
  /** Where presses and moves are watched; omitted outside a browser. */
  window?: Pick<Window, "addEventListener" | "removeEventListener">;
}

export interface OrbitHoverGate {
  isOrbiting(): boolean;
  /** For `useSyncExternalStore`: called on every change of `isOrbiting`. */
  subscribe(listener: () => void): () => void;
  /**
   * Wires the gate to its sources; reference counted, so every part of a
   * viewer can attach and the first one's sources serve them all. Returns the
   * detach.
   */
  attach(attachment: OrbitHoverGateAttachment): () => void;
}

export function createOrbitHoverGate(): OrbitHoverGate {
  let state = IDLE_ORBIT_HOVER_GATE;
  const listeners = new Set<() => void>();
  let attachments = 0;
  let unwire: (() => void) | null = null;
  let resync: OrbitHoverGateAttachment["resync"] | null = null;
  let pointer: OrbitHoverGatePointer | null = null;

  const dispatch = (event: OrbitHoverGateEvent) => {
    const wasOrbiting = state.orbiting;
    state = reduceOrbitHoverGate(state, event);
    if (state.orbiting === wasOrbiting) return;
    // Resync first: a hover the parts kept through the orbit is dropped
    // before they are told to show it again.
    if (!state.orbiting) resync?.(pointer);
    for (const listener of listeners) listener();
  };

  const wire = ({ source, window }: OrbitHoverGateAttachment) => {
    // Capture phase on the window: the press is recorded before
    // camera-controls' own listener raises the flag from it.
    const onPress = (event: PointerEvent) => {
      pointer = pointerOf(event);
      dispatch({ type: "press", x: event.clientX, y: event.clientY });
    };
    const onMove = (event: PointerEvent) => {
      pointer = pointerOf(event);
      if (state.controlling && !state.orbiting) {
        dispatch({ type: "move", x: event.clientX, y: event.clientY });
      }
    };
    const onGone = (event: PointerEvent) => {
      pointer = { ...pointerOf(event), hovers: false };
    };
    const onLift = (event: PointerEvent) => {
      pointer = pointerOf(event);
    };
    const onFlag = () =>
      dispatch({ type: "controlling", controlling: source.getControlling() });
    window?.addEventListener("pointerdown", onPress, true);
    window?.addEventListener("pointermove", onMove, true);
    window?.addEventListener("pointerup", onLift, true);
    window?.addEventListener("pointercancel", onGone, true);
    const unsubscribe = source.subscribe(onFlag);
    onFlag();
    return () => {
      window?.removeEventListener("pointerdown", onPress, true);
      window?.removeEventListener("pointermove", onMove, true);
      window?.removeEventListener("pointerup", onLift, true);
      window?.removeEventListener("pointercancel", onGone, true);
      unsubscribe();
      // The state stays as it is: every part can unmount at once mid-orbit
      // (a rebuild remounts them all), and the orbit, with its resync on
      // release, has to go on once they are back. The next wire reads the
      // flag again, so a control that ended in between closes the gate then.
      // Where the pointer went meanwhile is unknown until the next pointer
      // event, the release included, so until then it is not replayed.
      if (pointer) pointer = { ...pointer, hovers: false };
    };
  };

  return {
    isOrbiting: () => state.orbiting,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    attach: (attachment) => {
      attachments += 1;
      if (attachments === 1) {
        resync = attachment.resync;
        unwire = wire(attachment);
      }
      let attached = true;
      return () => {
        if (!attached) return;
        attached = false;
        attachments -= 1;
        if (attachments > 0) return;
        // No resync on the way out: nothing is left to light up.
        resync = null;
        unwire?.();
        unwire = null;
      };
    },
  };
}

const isInside = (rect: DOMRect, { x, y }: OrbitHoverGatePointer) =>
  x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

/**
 * Whether r3f's replay of the last event it handled still says what is under
 * the pointer: only if the pointer can hover, is inside the viewer, and is
 * where that event was. A finger that lifted, a pointer that left the viewer
 * or one that moved where r3f did not see it (over an overlay, or while no
 * part listened) would light a part the cursor is not on.
 */
export function canReplay(
  pointer: OrbitHoverGatePointer | null,
  surface: DOMRect,
  lastEvent: Pick<MouseEvent, "clientX" | "clientY"> | null,
): boolean {
  if (!pointer) return true;
  if (!pointer.hovers || !isInside(surface, pointer)) return false;
  // Within a pixel: r3f's last event is often the release's `click`, whose
  // coordinates Chrome rounds to whole pixels.
  return (
    !lastEvent ||
    (Math.abs(lastEvent.clientX - pointer.x) <= 1 &&
      Math.abs(lastEvent.clientY - pointer.y) <= 1)
  );
}

/** One gate per canvas, shared by every draggable part on it. */
const gates = new WeakMap<object, OrbitHoverGate>();

export function orbitHoverGateFor(canvas: object): OrbitHoverGate {
  let gate = gates.get(canvas);
  if (!gate) {
    gate = createOrbitHoverGate();
    gates.set(canvas, gate);
  }
  return gate;
}

/**
 * The orbit gate of the viewer this component renders in, attached for as
 * long as it is mounted. `null` outside a product viewer (no product store,
 * so no camera-control flag), where hover is never gated.
 */
export function useOrbitHoverGate(): OrbitHoverGate | null {
  const productStore = useContext(ProductStoreContext);
  const canvas = useThree((state) => state.gl.domElement);
  const get = useThree((state) => state.get);
  const gate = productStore ? orbitHoverGateFor(canvas) : null;

  useEffect(() => {
    if (!gate || !productStore) return;
    return gate.attach({
      source: {
        getControlling: () => productStore.getState().isUserControllingCamera,
        subscribe: (listener) =>
          productStore.subscribe((next, previous) => {
            if (
              next.isUserControllingCamera !== previous.isUserControllingCamera
            ) {
              listener();
            }
          }),
      },
      resync: (pointer) => {
        const { events, gl, internal } = get();
        const surface = events.connected ?? gl.domElement;
        if (
          canReplay(
            pointer,
            surface.getBoundingClientRect(),
            internal.lastEvent.current,
          )
        ) {
          events.update?.();
        } else {
          // The hover a part kept through the orbit is stale as well: the
          // pointer may have moved off it without r3f seeing a leave.
          events.handlers?.onPointerLeave(new Event("pointerleave"));
        }
      },
      window: globalThis.window,
    });
  }, [gate, productStore, get]);

  return gate;
}
